<template>
  <div class="mb-4 p-3 flex flex-col gap-y-2">
    <div v-if="reatlas.directions.error" class="mb-2 p-2 bg-red-100 border border-red-400 text-xs text-red-700 rounded">
      {{ reatlas.directions.error }}
    </div>
    <div class="flex items-center justify-between mb-2">
      <span class="font-bold">Directions</span>
      <div class="flex items-center gap-x-2">
        <button v-if="showClearButton" @click="clearDirections" class="text-xs text-red-600 hover:underline">Clear</button>
        <button @click="closeDirections()" class="flex items-center justify-between">
          <inline-svg name="close" classes="w-3 h-3 fill-current"></inline-svg>
        </button>
      </div>
    </div>
    <draggable v-model="stops" @end="onReorder" :move="onMove" handle=".drag-handle">
      <div v-for="(stop, idx) in stops" :key="stop.id" class="flex items-center mb-2 gap-x-2">

        <span class="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold text-xs mr-1 border border-blue-300">{{ markerLabel(idx) }}</span>
        <input class="text-xs flex-1" :ref="setStopInputRef(idx)" v-model="stop.text" type="text" :class="['form-input', errorHighlight.stops[idx] ? 'input-warning' : '']" :placeholder="stopPlaceholder(idx)" autocomplete="off" @input="clearRouteError" />
        <span
          class="drag-handle flex items-center justify-center ml-1"
          :class="stop.text ? 'cursor-move' : 'opacity-50 cursor-default'"
          :title="stop.text ? 'Drag to reorder' : 'Enter a value to enable drag'"
        >
          <inline-svg name="drag" classes="w-4 h-4 text-gray-400"></inline-svg>
        </span>
        <button @click="removeStop(idx)" class="ml-1 text-xs text-red-600 hover:underline flex items-center justify-center" v-if="canRemoveStop(idx)">
          <inline-svg name="close" classes="w-3 h-3 fill-current"></inline-svg>
        </button>
      </div>
    </draggable>
    <div class="flex items-center mb-2">
      <button
      v-if="showAddStopButton"
      @click="addStop"
      class="add-stop-link self-end flex items-center gap-x-1 w-full"
      type="button"
    >
      <inline-svg name="add" classes="w-4 h-4 text-blue-600 fill-current"></inline-svg>
      <span class="text-xs text-blue-600 ml-3">Add Stop</span>
    </button>
    </div>

    <!--Options to select time-->
    <div class="mt-3" v-if="showAddStopButton">
      <div class="flex items-center gap-4 mb-2">
        <div class="relative w-full">
          <select v-model="travelMode" class="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-2 px-4 pr-8 rounded leading-tight focus:outline-none focus:border-blue-500 text-sm">
            <option value="now">Leave now</option>
            <!-- <option value="google">Google Maps (no traffic)</option> -->
            <option value="depart">Depart at</option>
            <option value="arrive">Arrive by</option>
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
            </svg>
          </div>
        </div>
      </div>

      <div v-if="travelMode === 'depart' || travelMode === 'arrive'" class="flex gap-2 mt-2">
        <div class="flex-1">
          <input 
            type="date" 
            v-model="selectedDate" 
            class="w-full p-2 border rounded text-sm"
            :min="new Date().toISOString().split('T')[0]"
          >
        </div>
        <div class="flex-1">
          <input 
            type="time" 
            v-model="selectedTime" 
            class="w-full p-2 border rounded text-sm"
          >
        </div>
      </div>


    </div>



    <div v-if="routeDetails" class="mt-2 p-2 border border-gray-200 rounded text-xs bg-gray-100">
      <div><b>Distance:</b> {{ routeDetails.distance }}</div>
      <div><b>Duration:</b> {{ routeDetails.duration }}</div>
    </div>
    

    <div v-if="errorMessage" class="mt-2 p-2 bg-red-100 border border-red-400 text-xs text-red-700 rounded">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapState } from 'vuex';
import '../../stores/reatlas';
let stopIdCounter = 1;
function indexToLabel(idx) {
  let label = '';
  idx++;
  while (idx > 0) {
    let rem = (idx - 1) % 26;
    label = String.fromCharCode(65 + rem) + label;
    idx = Math.floor((idx - 1) / 26);
  }
  return label;
}
export default {
  name: 'DirectionForm',
  components: { draggable },
  data() {
    // Ensure we're using a time at least 5 minutes in the future to avoid past time issues
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // Add 5 minutes buffer
    
    const timeString = now.toTimeString().substring(0, 5);
    const dateString = now.toISOString().split('T')[0];
    
    return {
      stops: [
        { id: stopIdCounter++, text: '', latLng: null },
        { id: stopIdCounter++, text: '', latLng: null }
      ],
      routeDetails: null,
      directionsSet: false,
      autocompleteStops: [],
      ignoreAutocompleteUpdate: false,
      errorMessage: '',
      errorHighlight: {
        stops: []
      },
      travelMode: 'now',

      //code for time filter and dropdown
      selectedDate: dateString,
      selectedTime: timeString,
      calculatedDepartureTime: null,
      debounceTimer: null,
      isCalculatingRoute: false, // Flag to prevent concurrent calculations

    };
  },
  computed: {
    ...mapState(['reatlas']),
    canSubmitDirections() {
      return this.stops.length >= 2 && this.stops.every(s => s.text && s.latLng);
    },
    showAddStopButton() {
      if (this.errorMessage) return false;
      const lastStop = this.stops[this.stops.length - 1];
      return lastStop && lastStop.text && lastStop.text.trim().length > 0;
    },
    routeIsValid() {
      return !this.errorMessage && this.directionsSet;
    },
    showClearButton() {
      // Show if origin is set (text or latLng), or if there is any error
      const origin = this.stops[0];
      return (
        (origin && (origin.text && origin.text.trim() || origin.latLng)) ||
        this.errorMessage ||
        this.reatlas.directions.error
      );
    }
  },
  watch: {

    stops: {
    deep: true,
    handler(newStops, oldStops) {
      // Prevent infinite loop by checking if stops actually changed
      if (JSON.stringify(newStops) === JSON.stringify(oldStops)) {
        return;
      }
      
      console.log(newStops,'newStops');
      // Check if both origin and destination have text and latLng
      const hasOrigin = newStops[0]?.text && newStops[0]?.latLng;
      const hasDestination = newStops[1]?.text && newStops[1]?.latLng;
      console.log('directionsSet:', this.directionsSet, 'hasOrigin:', hasOrigin, 'hasDestination:', hasDestination);
      
      // Only trigger if this is the first time both are set (not already directionsSet)
      if (hasOrigin && hasDestination && !this.directionsSet) {
        this.travelMode = 'now';
        this.$nextTick(() => {
          this.calculateDistanceAndTime();
        });
      }
    }
  },


    'reatlas.directions.stops': {
      handler(newStops) {
        if (!Array.isArray(newStops) || newStops.length < 2) {
          this.stops = [
            { id: stopIdCounter++, text: '', latLng: null },
            { id: stopIdCounter++, text: '', latLng: null }
          ];
          this.autocompleteStops = [];
          this.$nextTick(() => {
            this.initDirectionsAutocomplete();
          });
          return;
        }
        // Always assign new unique IDs to each stop when syncing from store
        this.stops = newStops.map((s) => ({
          id: stopIdCounter++,
          text: s.text || '',
          latLng: s.latLng || null
        }));
        this.autocompleteStops = [];
        this.stops.forEach((stop, idx) => {
          this.reverseGeocode(stop.latLng, idx);
        });
        this.$nextTick(() => {
          this.initDirectionsAutocomplete();
        });
      },
      deep: true
    },
    'reatlas.directions.directionsRenderer': {
      handler(dr, oldDr) {
        if (dr && dr.getDirections) {
          // Remove old listener if it exists
          if (oldDr && this._directionsChangedListener) {
            google.maps.event.removeListener(this._directionsChangedListener);
            this._directionsChangedListener = null;
          }
          
          const updateRoute = () => {
            const result = dr.getDirections();

            if (result && result.routes && result.routes[0] && result.routes[0].legs) {
              this.directionsSet = true;
              let totalDistance = 0, totalDuration = 0;
              
              result.routes[0].legs.forEach(leg => {
                totalDistance += leg.distance.value;
                // Use duration_in_traffic if available (when drivingOptions are used), otherwise use duration
                totalDuration += (leg.duration_in_traffic?.value || leg.duration.value);
              });
              this.routeDetails = {
                distance: this.formatDistance(totalDistance),
                duration: this.formatDuration(totalDuration)
              };
              
              // Emit event to parent to update button visibility
              this.$emit('route-updated', { valid: true });
              
              // Prevent infinite loop: only update stops if they're actually different
              
              if (result.request && result.request.origin && result.routes[0].legs[0]) {
                const newOriginText = result.routes[0].legs[0].start_address;
                if (this.stops[0].text !== newOriginText) {
                  this.stops[0].text = newOriginText;
                  this.stops[0].latLng = result.request.origin.location || this.stops[0].latLng;
                }
              }
              if (result.request && result.request.destination && result.routes[0].legs.length) {
                const lastLeg = result.routes[0].legs[result.routes[0].legs.length - 1];
                const newDestText = lastLeg.end_address;
                const destIdx = this.stops.length - 1;
                if (this.stops[destIdx].text !== newDestText) {
                  this.stops[destIdx].text = newDestText;
                  this.stops[destIdx].latLng = result.request.destination.location || this.stops[destIdx].latLng;
                }
              }
              if (result.request && Array.isArray(result.request.waypoints) && result.request.waypoints.length) {
                result.request.waypoints.forEach((wp, idx) => {
                  if (this.stops[idx + 1]) {
                    this.stops[idx + 1].latLng = wp.location;
                    if (result.routes[0].legs[idx + 1]) {
                      const newWpText = result.routes[0].legs[idx + 1].start_address;
                      if (this.stops[idx + 1].text !== newWpText) {
                        this.stops[idx + 1].text = newWpText;
                      }
                    }
                  }
                });
              }
            }
          };
          
          // Store the listener reference so we can remove it later
          this._directionsChangedListener = google.maps.event.addListener(dr, 'directions_changed', updateRoute);
          updateRoute();
        }
      },
      immediate: true
    },

    //code for time filter and dropdown
    travelMode() {
      if (this.canSubmitDirections) {
        // Don't debounce travel mode changes - apply immediately
        this.calculateDistanceAndTime();
      }
    },
    selectedDate() {
      if ((this.travelMode === 'depart' || this.travelMode === 'arrive') && this.canSubmitDirections) {
        // Debounce date changes to avoid excessive API calls
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
          this.calculateDistanceAndTime();
        }, 300);
      }
    },
    selectedTime() {
      if ((this.travelMode === 'depart' || this.travelMode === 'arrive') && this.canSubmitDirections) {
        // Debounce time changes to avoid excessive API calls
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
          this.calculateDistanceAndTime();
        }, 300);
      }
    }
  },
  methods: {
    closeDirections() {
      this.reatlas.layers.directions.isOn = false;
      this.clearDirections()
    },

    //code for time filter and dropdown
    async calculateDistanceAndTime() {
      // Prevent concurrent calculations
      if (this.isCalculatingRoute) {
        console.log('[DirectionForm] Route calculation already in progress, skipping');
        return;
      }
      
      if (!this.canSubmitDirections) return;

      this.isCalculatingRoute = true;

      try {
        // Build driving options based on travel mode
        let drivingOptions = null;

        if (this.travelMode === 'now') {
          // For "Leave now", use current time
          const now = new Date();
          drivingOptions = {
            departureTime: now,
            trafficModel: google.maps.TrafficModel.BEST_GUESS
          };
        } 
        else if (this.travelMode === 'depart') {
          const [year, month, day] = this.selectedDate.split('-').map(Number);
          const [hours, minutes] = this.selectedTime.split(':').map(Number);
          let departureTime = new Date(year, month - 1, day, hours, minutes);
          
          // Ensure the departure time is in the future
          const now = new Date();
          if (departureTime <= now) {
            // If the selected time is in the past, use current time + 5 minutes
            departureTime = new Date(now.getTime() + 5 * 60 * 1000);
            // Update the form fields to reflect the adjusted time
            this.selectedDate = departureTime.toISOString().split('T')[0];
            this.selectedTime = departureTime.toTimeString().substring(0, 5);
          }
          
          drivingOptions = {
            departureTime: departureTime,
            trafficModel: google.maps.TrafficModel.BEST_GUESS
          };
        }
        else if (this.travelMode === 'arrive') {
          // For "Arrive by", handle it separately
          await this.handleArriveBy();
          return;
        }

        // Call the Vuex action to recalculate directions with driving options
        await this.$store.dispatch('fetchDirectionsWithWaypoints', {
          stops: this.stops,
          drivingOptions: drivingOptions
        });
        this.errorMessage = '';
      } catch (error) {
        console.error('Directions API error:', error);
        this.errorMessage = 'Error calculating route. Please try again.';
        this.directionsSet = false;
        this.$emit('route-updated', { valid: false });
      } finally {
        this.isCalculatingRoute = false;
      }
    },

async handleArriveBy() {
      // Prevent concurrent calculations
      if (this.isCalculatingRoute) {
        console.log('[DirectionForm] Route calculation already in progress, skipping arrive by');
        return;
      }
      
      this.isCalculatingRoute = true;
      
      try {
        // First, get the current route duration to estimate departure time
        const dr = this.reatlas?.directions?.directionsRenderer;
        const currentDirections = dr && dr.getDirections ? dr.getDirections() : null;
        
        let estimatedDurationSeconds = 0;
        if (currentDirections && currentDirections.routes && currentDirections.routes[0] && currentDirections.routes[0].legs) {
          currentDirections.routes[0].legs.forEach(leg => {
            estimatedDurationSeconds += (leg.duration?.value || 0);
          });
        }
        
        // If we don't have a current route, make a quick call to get baseline duration
        if (estimatedDurationSeconds === 0) {
          const result = await this.$store.dispatch('fetchDirectionsWithWaypoints', {
            stops: this.stops,
            drivingOptions: null
          });
          if (result && result.routes && result.routes[0] && result.routes[0].legs) {
            result.routes[0].legs.forEach(leg => {
              estimatedDurationSeconds += (leg.duration?.value || 0);
            });
          }
        }
        
        // Calculate the departure time needed to arrive at the desired time
        const [year, month, day] = this.selectedDate.split('-').map(Number);
        const [hours, minutes] = this.selectedTime.split(':').map(Number);
        const arrivalTime = new Date(year, month - 1, day, hours, minutes).getTime();
        
        // Calculate departure time (arrival time - duration)
        const departureTime = new Date(arrivalTime - (estimatedDurationSeconds * 1000));
        
        // Store the calculated departure time for display
        this.calculatedDepartureTime = departureTime.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
        
        // Now recalculate the route with the calculated departure time
        await this.$store.dispatch('fetchDirectionsWithWaypoints', {
          stops: this.stops,
          drivingOptions: {
            departureTime: departureTime,
            trafficModel: google.maps.TrafficModel.BEST_GUESS
          }
        });
        
        this.errorMessage = '';
      } catch (error) {
        console.error('Arrive by calculation error:', error);
        this.errorMessage = 'Error calculating arrival time. Please try again.';
        this.directionsSet = false;
        this.$emit('route-updated', { valid: false });
      } finally {
        this.isCalculatingRoute = false;
      }
    },



    markerLabel(idx) {
      return indexToLabel(idx);
    },
    stopPlaceholder(idx) {
      if (idx === 0) return 'Enter origin address';
      if (idx === this.stops.length - 1) return 'Enter destination address';
      return `Stop ${this.markerLabel(idx)}`;
    },
    formatDistance(meters) {
      const miles = meters / 1609.344;
      const rounded = Math.round(miles);
      return rounded.toLocaleString() + ' Miles';
    },
    formatDuration(seconds) {
      const totalMinutes = Math.round(seconds / 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      let result = '';
      if (hours > 0) {
        result += hours + ' Hour' + (hours !== 1 ? 's' : '');
      }
      if (minutes > 0) {
        if (result) result += ' ';
        result += minutes + ' Minute' + (minutes !== 1 ? 's' : '');
      }
      return result || '0 Minutes';
    },
    setStopInputRef(idx) {
      return el => {
        if (el && !this.autocompleteStops[idx]) {
          this.initStopAutocomplete(idx, el);
        }
      };
    },
    addStop() {
      if (this.errorMessage) return;
      const lastStop = this.stops[this.stops.length - 1];
      if (!lastStop.text || !lastStop.text.trim()) return;
      const newStop = { id: stopIdCounter++, text: '', latLng: null };
      this.stops.push(newStop);
      this.$nextTick(() => {
        this.initDirectionsAutocomplete();
        const idx = this.stops.length - 1;
        const input = this.$refs[`stopInput${idx}`];
        if (input && input[0]) input[0].focus();
      });
    },
    canRemoveStop(idx) {
      if (idx === this.stops.length - 1 && this.stops.length > 2 && !this.stops[idx].text) {
        return true;
      }
      return (this.stops[idx].text && this.stops.length > 2);
    },
    async removeStop(idx) {
      if (!this.canRemoveStop(idx)) return;
      // Save current stops in case we need to revert
      const prevStops = this.stops.slice();
      // Call Vuex action to attempt removal
      try {
        await this.$store.dispatch('removeDirectionPoint', idx);
        // If successful, update local stops from store
        const newStops = this.reatlas.directions.stops;
        // Only update if the number of stops decreased (removal succeeded)
        if (newStops.length < prevStops.length) {
          this.stops = newStops.map((s) => ({
            id: stopIdCounter++,
            text: s.text || '',
            latLng: s.latLng || null
          }));
          this.autocompleteStops.splice(idx, 1);
          this.errorHighlight.stops.splice(idx, 1);
          this.errorMessage = '';
        } else {
          // Route failed, do not remove stop, show error
          this.errorMessage = this.reatlas.directions.error || 'Unable to find a route after removing this stop. Please try a different route.';
        }
      } catch (e) {
        // Defensive: should not happen, but just in case
        this.errorMessage = 'An error occurred while removing the stop.';
      }
    },
    async onReorder() {
      const normalizedStops = this.stops.map((stop) => ({
        ...stop,
        latLng: this.normalizeLatLng(stop.latLng)
      }));

      try {
        await this.$store.dispatch('reorderDirectionStops', { stops: normalizedStops });
        this.calculateDirections();

        this.errorMessage = '';
      } catch (err) {
        this.errorMessage = this.reatlas.directions.error || 'Unable to update route with the new stop order. Please try again.';
      }

      this.stops = normalizedStops.map((s) => ({
        id: stopIdCounter++,
        text: s.text || '',
        latLng: s.latLng || null,
        place_id: s.place_id || null,
        meta: s.meta || null
      }));
      this.errorHighlight.stops = new Array(this.stops.length).fill(false);
      this.autocompleteStops = [];
      this.$nextTick(() => {
        this.initDirectionsAutocomplete();
      });
    },
    initDirectionsAutocomplete() {
      if (!window.google || !window.google.maps) return;
      this.stops.forEach((stop, idx) => {
        const refName = `stopInput${idx}`;
        if (this.$refs[refName] && !this.autocompleteStops[idx]) {
          this.initStopAutocomplete(idx, this.$refs[refName][0]);
        }
      });
    },
    initStopAutocomplete(idx, el) {
      if (!window.google || !window.google.maps) return;
      if (this.autocompleteStops[idx]) return;
      const autocomplete = new google.maps.places.Autocomplete(el, { types: ['geocode'] });
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          this.$store.dispatch('setDirectionPoint', { latLng: place.geometry.location, markerData: null });
        }
      });
      this.$set(this.autocompleteStops, idx, autocomplete);
    },
    async reverseGeocode(latLng, stopIdx) {
      if (!window.google || !window.google.maps) return;
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK' && results[0]) {
          this.$set(this.stops, stopIdx, {
            ...this.stops[stopIdx],
            text: results[0].formatted_address
          });
        }
      });
    },
    normalizeLatLng(latLng) {
      if (!latLng) return null;
      if (typeof latLng.lat === 'function' && typeof latLng.lng === 'function') {
        return latLng;
      }
      const latValue = typeof latLng.lat === 'number' ? latLng.lat : parseFloat(latLng.lat);
      const lngValue = typeof latLng.lng === 'number' ? latLng.lng : parseFloat(latLng.lng);

      if (Number.isNaN(latValue) || Number.isNaN(lngValue)) {
        return null;
      }

      if (window.google && window.google.maps && window.google.maps.LatLng) {
        return new window.google.maps.LatLng(latValue, lngValue);
      }

      return { lat: latValue, lng: lngValue };
    },
    clearRouteError() {
      if (this.reatlas.directions.error) {
        this.$store.state.reatlas.directions.error = null;
      }
      this.errorMessage = '';
    },
    clearDirections() {
  console.log('clearDirections called');
  
  try {
    // 1. First, clear any existing directions data
    if (this.reatlas?.directions) {
      // Clear the renderer if it exists
      if (this.reatlas.directions.directionsRenderer) {
        try {
          // Set an empty directions object first
          this.reatlas.directions.directionsRenderer.setDirections({
            routes: [{
              legs: [{
                distance: { text: '0 mi', value: 0 },
                duration: { text: '0 mins', value: 0 }
              }]
            }]
          });
          // Then clear the map
          this.reatlas.directions.directionsRenderer.setMap(null);
        } catch (e) {
          console.warn('Error clearing directions renderer:', e);
        }
      }
      
      // Clear the directions data
      this.reatlas.directions.directions = null;
      this.reatlas.directions.routes = [];
    }

    // 2. Reset stops
    this.stops = [
      { id: stopIdCounter++, text: '', latLng: null, place_id: null },
      { id: stopIdCounter++, text: '', latLng: null, place_id: null }
    ];
    
    // 3. Clear local state
    this.autocompleteStops = [];
    this.routeDetails = null;
    this.directionsSet = false;
    this.errorMessage = '';
    this.calculatedDepartureTime = null;
    
    // Emit event to parent
    this.$emit('route-updated', { valid: false });
    
    // 4. Reset date and time to current
    const now = new Date();
    this.selectedDate = now.toISOString().split('T')[0];
    this.selectedTime = now.toTimeString().substring(0, 5);
    this.travelMode = 'now';
    
    // 5. Reset Vuex store
    if (this.$store?.state?.reatlas?.directions) {
      this.$store.state.reatlas.directions.stops = [];
      this.$store.state.reatlas.directions.error = null;
      this.$store.state.reatlas.directions.routes = [];
      this.$store.state.reatlas.directions.directions = null;
    }

    // 6. Reset map state
    this.$nextTick(() => {
      this.$store.dispatch('disableDirectionsMode');
      this.$nextTick(() => {
        this.$store.dispatch('enableDirectionsMode');
        this.$forceUpdate();
      });
    });

    console.log('clearDirections completed');
  } catch (error) {
    console.error('Error in clearDirections:', error);
    this.errorMessage = 'Error clearing directions. Please refresh the page.';
  }
},

    clearDirections2() {
      console.log('clearDirections');
      if (this.reatlas?.directions?.directionsRenderer) {
      try {
        this.reatlas.directions.directionsRenderer.setMap(null);
        this.reatlas.directions.directionsRenderer.setDirections({ routes: [] });
      } catch (e) {
        console.warn('Error clearing directions renderer:', e);
      }
    }

      this.stops = [
        { id: stopIdCounter++, text: '', latLng: null },
        { id: stopIdCounter++, text: '', latLng: null }
      ];
      this.autocompleteStops = [];
      this.routeDetails = null;
      this.directionsSet = false;
      this.$store.state.reatlas.directions.stops = [];
      this.$store.state.reatlas.directions.error = null;
      this.errorMessage = '';
      if (this.reatlas.directions.directionsRenderer) {
        this.reatlas.directions.directionsRenderer.set('directions', null);
      }

      this.travelMode = 'now';
      this.selectedDate = new Date().toISOString().split('T')[0];
      this.selectedTime = new Date().toTimeString().substring(0, 5);
      this.calculatedDepartureTime = null;

      console.log('clearDirections completed', {
        selectedDate: this.selectedDate,
        selectedTime: this.selectedTime,
        travelMode: this.travelMode
      });

      this.$store.dispatch('disableDirectionsMode');
      this.$store.dispatch('enableDirectionsMode');
      this.$forceUpdate();
      console.log('clearDirections completed', {
        selectedDate: this.selectedDate,
        selectedTime: this.selectedTime,
        travelMode: this.travelMode
      });

    },
    onMove(evt) {
      // Only allow drag if both source and target stops have a value
      const dragged = this.stops[evt.oldIndex];
      const target = this.stops[evt.newIndex];
      return dragged && dragged.text && target && target.text;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initDirectionsAutocomplete();
    });
  },
  updated() {
    this.initDirectionsAutocomplete();
  },
  beforeDestroy() {
    // Clean up the directions_changed listener
    if (this._directionsChangedListener) {
      google.maps.event.removeListener(this._directionsChangedListener);
      this._directionsChangedListener = null;
    }
    
    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
};
</script>

<style scoped>
.add-stop-link {
  background: none;
  border: none;
  padding: 0;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.15s;
}
.add-stop-link:hover .text-blue-600,
.add-stop-link:focus .text-blue-600 {
  text-decoration: underline;
  color: #2563eb; /* Tailwind blue-600 */
}
.add-stop-link:focus {
  outline: none;
}
.input-warning {
  border-color: #f87171 !important; /* Tailwind red-400 */
  background-color: #fef2f2 !important; /* Tailwind red-50 */
}
</style>
